import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SynchronizedAreaChart2 from '../Elements/Chart2';
import { DotLoader } from '../Elements/LogoLoader';
import RevenueReward from '../machinedata/revenue/Reward';
import RevenuePayouts from '../machinedata/revenue/Payouts';
import PublicMinerWorkers, { PublicWorker } from './PublicMinerWorkers';
import { ChartPoint, HashPoint, formatCoinAmount, minerApiPath, toChartData } from './publicMinerApi';

// segments-pool PublicMinerDashboardDto
interface MinerDashboard {
    userName: string;
    coin: string;
    totalPayout: number | null;
    revenueYesterday: number | null;
    revenueTodayEst: number | null;
    balance: number | null;
    hashRate24: number | null;
    minersOnline: number | null;
    minersOffline: number | null;
    hashrate: HashPoint[] | null;
}

// segments-pool RewardDto, limited to segproxy rewards and payouts
interface MinerRewards {
    totalBalance: number;
    rewards: any[];
    payouts: any[];
}

interface WorkerResponse {
    all: number;
    online: number;
    offline: number;
    data: PublicWorker[] | null;
}

type Period = '1' | '24';
type Section = 'WORKERS' | 'REVENUE' | 'PAYOUTS';

const COIN = 'BTC';

const isNotFound = (error: any): boolean => error?.response?.status === 404;

/** The public dashboard of a segproxy miner's account: segproxy workers, rewards and payouts only. */
const PublicMinerDashboard: React.FC<{ address: string }> = ({ address }) => {
    const [period, setPeriod] = useState<Period>('1');
    const [dashboard, setDashboard] = useState<MinerDashboard | null>(null);
    const [chartData, setChartData] = useState<ChartPoint[]>([]);
    const [chartLoading, setChartLoading] = useState(false);
    const [rewards, setRewards] = useState<MinerRewards | null>(null);
    const [workers, setWorkers] = useState<PublicWorker[]>([]);
    const [workersLoading, setWorkersLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [activeSection, setActiveSection] = useState<Section>('WORKERS');

    useEffect(() => {
        setChartLoading(true);
        axios.get<MinerDashboard>(`${minerApiPath(address)}/dashboard?hours=${period}`)
            .then(response => {
                setDashboard(response.data);
                setChartData(toChartData(response.data?.hashrate));
            })
            .catch(error => {
                if (isNotFound(error)) {
                    setNotFound(true);
                } else {
                    console.error('Error fetching miner dashboard:', error);
                    setChartData([]);
                }
            })
            .finally(() => setChartLoading(false));
    }, [address, period]);

    useEffect(() => {
        setWorkersLoading(true);
        axios.get<WorkerResponse>(`${minerApiPath(address)}/workers`)
            .then(response => setWorkers(response.data?.data ?? []))
            .catch(error => {
                if (isNotFound(error)) {
                    setNotFound(true);
                } else {
                    console.error('Error fetching miner workers:', error);
                }
            })
            .finally(() => setWorkersLoading(false));

        axios.get<MinerRewards>(`${minerApiPath(address)}/rewards`)
            .then(response => setRewards(response.data))
            .catch(error => {
                if (isNotFound(error)) {
                    setNotFound(true);
                } else {
                    console.error('Error fetching miner rewards:', error);
                }
            });
    }, [address]);

    if (notFound) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-24 px-4">
                <p className="text-2xl md:text-3xl font-black text-[#0E4D92] dark:text-blue-400 mb-3">Miner not found</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                    No miner is registered for this wallet address yet. Connect a miner using your wallet address as
                    the worker name, for example <span className="font-mono break-all">{address || '<address>'}.001</span>.
                </p>
            </div>
        );
    }

    const loadingCards = dashboard === null;
    const sections: { id: Section; label: string; count: number }[] = [
        { id: 'WORKERS', label: 'Workers', count: workers.length },
        { id: 'REVENUE', label: 'Revenue', count: rewards?.rewards?.length ?? 0 },
        { id: 'PAYOUTS', label: 'Payouts', count: rewards?.payouts?.length ?? 0 },
    ];

    return (
        <div className="md:py-8 pb-6">
            <div className="flex flex-col lg:flex-row gap-6 p-4">
                {/* Left Side: Stats Cards */}
                <div className="lg:w-1/4">
                    <div className="grid grid-cols-2 lg:flex lg:flex-col gap-3 lg:gap-4">
                        <DataCard loading={loadingCards} title={`Total Payout (${COIN})`} value={formatCoinAmount(dashboard?.totalPayout)} icon="💰" />
                        <DataCard loading={loadingCards} title={`Yesterday Revenue (${COIN})`} value={formatCoinAmount(dashboard?.revenueYesterday)} icon="📈" />
                        <DataCard loading={loadingCards} title={`Today's Est. Revenue (${COIN})`} value={formatCoinAmount(dashboard?.revenueTodayEst)} icon="📊" />
                        <DataCard loading={loadingCards} title={`Balance (${COIN})`} value={formatCoinAmount(dashboard?.balance)} icon="💳" />
                    </div>
                </div>

                {/* Right Side: Graph */}
                <div className="lg:w-3/4 flex flex-col">
                    <div className="relative md:p-10 md:rounded-[2.5rem] md:border md:border-white/40 md:dark:border-slate-700/50 transition-all h-full flex flex-col">
                        {chartLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 rounded-2xl">
                                <DotLoader />
                            </div>
                        )}
                        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                            <div className="text-center md:text-left mb-6 md:mb-0">
                                <p className="text-2xl md:text-4xl font-black text-[#0E4D92] dark:text-blue-400 mb-2 transition-colors tracking-tighter">Hashrate</p>
                                <p className="text-xs md:text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] opacity-80">Real-time mining power</p>
                            </div>
                            <div className="flex bg-slate-100/50 dark:bg-slate-900/50 backdrop-blur-sm p-1.5 rounded-2xl shadow-inner border border-white/10 dark:border-slate-700/30">
                                <ButtonToggle label="1 hour" isActive={period === '1'} disabled={chartLoading} onClick={() => setPeriod('1')} />
                                <ButtonToggle label="24 hours" isActive={period === '24'} disabled={chartLoading} onClick={() => setPeriod('24')} />
                            </div>
                        </div>
                        <div className="flex-grow bg-white/30 dark:bg-slate-900/40 rounded-3xl p-4 border border-white/20 dark:border-slate-800/50 shadow-inner">
                            <SynchronizedAreaChart2 data={chartData} />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-center items-center gap-4">
                        <span className="inline-flex items-center gap-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-4 py-2 rounded-full border border-green-200 dark:border-green-500/30 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="font-medium text-sm">Online:</span>
                            <span className="font-bold text-lg">{dashboard?.minersOnline ?? 0}</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 rounded-full border border-red-200 dark:border-red-500/30 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="font-medium text-sm">Offline:</span>
                            <span className="font-bold text-lg">{dashboard?.minersOffline ?? 0}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="mt-8 px-4">
                <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-3 mb-6 bg-slate-50 dark:bg-slate-900/50 p-1.5 md:p-2 rounded-2xl w-full md:w-fit border border-gray-100 dark:border-slate-800">
                    {sections.map(section => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all duration-300 flex-1 md:flex-none min-w-0 overflow-hidden ${activeSection === section.id
                                ? 'bg-[#0A3A6B] text-white shadow-md md:shadow-lg shadow-blue-200/50 dark:shadow-none'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-[#0A3A6B] dark:hover:text-blue-300'
                                }`}
                        >
                            <span className="whitespace-nowrap truncate text-left flex-1">{section.label}</span>
                            <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-bold flex-shrink-0 ${activeSection === section.id
                                ? 'bg-white/20 text-white'
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                                }`}>
                                {section.count}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="bg-white dark:bg-slate-800/40 rounded-[2rem] border border-gray-100 dark:border-slate-800/50 shadow-sm overflow-hidden">
                    {activeSection === 'WORKERS' && <PublicMinerWorkers address={address} workers={workers} loading={workersLoading} />}
                    {activeSection === 'REVENUE' && (
                        <div className="p-4 md:p-6">
                            <RevenueReward rewards={rewards?.rewards ?? []} coin={COIN} />
                        </div>
                    )}
                    {activeSection === 'PAYOUTS' && (
                        <div className="p-4 md:p-6">
                            <RevenuePayouts payouts={rewards?.payouts ?? []} coin={COIN} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DataCard: React.FC<{ title: string; value: string; loading: boolean; icon?: string }> = ({ title, value, loading, icon }) => (
    <div className="bg-blue-50 dark:bg-blue-900/20 backdrop-blur-sm p-4 md:p-6 lg:p-7 rounded-2xl md:rounded-[1.75rem] lg:rounded-[2rem] shadow-sm border border-blue-100 dark:border-blue-800/50 group hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-1 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-blue-100/30 to-blue-200/10 dark:from-blue-800/10 dark:to-blue-900/5 rounded-full -mr-8 lg:-mr-12 -mt-8 lg:-mt-12 blur-xl group-hover:scale-110 transition-transform duration-300" />
        <div className="absolute bottom-0 left-0 w-12 h-12 bg-blue-50/20 dark:bg-blue-800/10 rounded-full -ml-4 -mb-4 blur-lg" />
        <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
                <p className="text-[10px] md:text-[11px] lg:text-xs font-bold text-blue-600/80 dark:text-blue-400/80 uppercase tracking-[0.15em] flex-1 break-words">
                    {title}
                </p>
                {icon && <span className="text-lg lg:text-xl ml-2 opacity-80 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">{icon}</span>}
            </div>
            <div className="flex items-baseline gap-1 min-w-0">
                <h3 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-black text-[#0A3A6B] dark:text-blue-400 transition-colors tracking-tight break-words w-full">
                    {loading ? (
                        <div className="h-6 lg:h-8 w-20 lg:w-32 bg-blue-100 dark:bg-blue-800/40 rounded-lg animate-pulse"></div>
                    ) : (
                        value
                    )}
                </h3>
            </div>
            <div className="h-0.5 w-8 bg-gradient-to-r from-blue-400/50 to-blue-300/30 dark:from-blue-500/40 dark:to-blue-400/20 rounded-full mt-4 lg:mt-5 group-hover:w-12 transition-all duration-300" />
        </div>
    </div>
);

const ButtonToggle: React.FC<{ label: string; isActive: boolean; disabled: boolean; onClick: () => void }> = ({ label, isActive, disabled, onClick }) => (
    <button
        onClick={() => !disabled && onClick()}
        className={`px-6 py-2 rounded-xl text-xs font-bold transition-all border ${isActive
            ? 'bg-[#0E4D92] text-white border-[#0E4D92] shadow-md shadow-blue-200 dark:shadow-none scale-105'
            : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-gray-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-slate-600'
            }`}
        disabled={isActive}
    >
        {label}
    </button>
);

export default PublicMinerDashboard;
